import {
	ContentExpander,
	Grid,
	NumberField,
} from '@components';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
	AllRateCardFuelLevyModifiers,
	AllRateCardSettingFuelLevyModifiers,
	RateCardFormErrors,
	RateCardSettingsTypes, RateCardTypes
} from '@state';
import { RateCardType, TRateCardModifierSet } from '@types';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { useRecoilValue } from 'recoil';
import styles from './Modifiers.module.scss';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	type: RateCardType;
	isDisabled?: boolean;
	itemTypeId?: string;
}

export const OtherModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		itemTypeId
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		itemTypeId,
		isSettings: !!isSettings,
		type
	});
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const clearRCSettingsError = useClearRCSettingsError();

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const updateTimeframeModifier = (
		modifierKey: keyof TRateCardModifierSet,
		newValue: string
	) => {
		const update = {
			...modifiers,
			[modifierKey]: {
				type: 'FIXED',
				value: newValue,
				attracts_fuel_levy: !!fuelLevyModifiers?.[modifierKey]?.attracts_fuel_levy
			}
		} as TRateCardModifierSet;
		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardModifiers(newModifiers);
		clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.${modifierKey}`)

	}

	const handleOnBlur = (
		modifierKey: keyof TRateCardModifierSet,
		newValue: string
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateTimeframeModifier(modifierKey, value);
		}
		if (newValue.startsWith('.')) {
			updateTimeframeModifier(modifierKey, `0${newValue}`)
		}
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.[key]?.value?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		const fieldsWithPossibleError = [
			// 'fuel_levy_adjustment',
			'admin_fee', 'tolls'
		]
		return fieldsWithPossibleError.map((field) =>
			hasError(field)
		).some((field) => field.length > 0);
	}

	const getConfiguredCount = () => {
		const countedFields = ['tolls', 'admin_fee'] as const;
		return countedFields.reduce((count, field) => {
			const value = modifiers?.[field]?.value;
			return parseFloat(`${value ?? ''}`) > 0 ? count + 1 : count;
		}, 0);
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Other
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer={false}
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Grid columns={3} columnTemplate={'repeat(2, 1fr)'} gap={'16px'} alignItems='end'>
					{/* <NumberField
						name={'fuel_levy_adjustment'}
						value={`${modifiers?.fuel_levy_adjustment?.value}`}
						onChange={(e) => updateTimeframeModifier('fuel_levy_adjustment', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Fuel levy adjustment'}
						keepErrorSpace={false}
						errorMessage={hasError('fuel_levy_adjustment')}
						hasError={!!hasError('fuel_levy_adjustment')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('fuel_levy_adjustment', e.target.value)}
					/> */}
					<NumberField
						name={'tolls'}
						value={`${modifiers?.tolls?.value}`}
						onChange={(e) => updateTimeframeModifier('tolls', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Tolls'}
						keepErrorSpace={false}
						errorMessage={hasError('tolls')}
						hasError={!!hasError('tolls')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('tolls', e.target.value)}
					/>
					<NumberField
						name={'admin_fee'}
						value={`${modifiers?.admin_fee?.value}`}
						onChange={(e) => updateTimeframeModifier('admin_fee', e.target.value)}
						leftModule={<span className={styles.prefix}>$</span>}
						label={'Admin fee'}
						keepErrorSpace={false}
						errorMessage={hasError('admin_fee')}
						hasError={!!hasError('admin_fee')}
						disabled={isDisabled}
						min={'0'}
						onBlur={(e) => handleOnBlur('admin_fee', e.target.value)}
					/>
				</Grid>
			</div>
		</ContentExpander>
	);
}